import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AttestationDecision, GlobalInvitation, formatRoleLabel } from '@promo/shared';
import * as api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { CenteredLayout } from '@/components/layout/CenteredLayout';
import { parseNameFromEmail } from '@promo/shared';

export function AttestInvitationPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const { data: invitation, isLoading: isLoadingQuery, error } = useQuery<GlobalInvitation, Error>({
    queryKey: ['globalInvitation', token],
    queryFn: () => api.get<GlobalInvitation>('/global-invitations/attest', { token: token! }),
    enabled: !!token,
  });

  const mutation = useMutation<GlobalInvitation, Error, AttestationDecision>({
    mutationFn: (decision) => api.put<GlobalInvitation>('/global-invitations/attest', { token, decision }),
    onSuccess: () => {
      toast({ title: 'Response recorded' });
      navigate('/');
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Failed to submit your decision';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    },
  });

  if (isLoadingQuery) {
    return (
      <CenteredLayout
        title="Loading..."
        subtitle="Please wait while we load the request."
      >
        <div />
      </CenteredLayout>
    );
  }
  if (error || !invitation) {
    return (
      <CenteredLayout title="Failed to load request.">
        <div />
      </CenteredLayout>
    );
  }

  // Parse first and last name from the invitee's email
  const parsedName = parseNameFromEmail(invitation.inviteeEmail);
  const inviteeName = parsedName ?? invitation.inviteeEmail;
  const formattedRole = formatRoleLabel(invitation.roleRequested);

  return (
    <CenteredLayout
      title="Peer Attestation"
      subtitle={`Attest whether ${inviteeName} should be a ${formattedRole}.`}
    >
      <div className="flex justify-center space-x-4">
        <Button
          variant="default"
          onClick={() => mutation.mutate(AttestationDecision.ATTESTED)}
          disabled={mutation.isPending}
        >
          Attest
        </Button>
        <Button
          variant="destructive"
          onClick={() => mutation.mutate(AttestationDecision.DENIED)}
          disabled={mutation.isPending}
        >
          Deny
        </Button>
      </div>
    </CenteredLayout>
  );
} 